// frontend/src/features/aw/AWImportWizard.jsx
import React, { useState, useRef } from "react";
import { importAccounts } from "./api";

export default function AWImportWizard({ onDone }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef();

  const handleUpload = async () => {
    if (!file) return;
    setLoading(true); setError(""); setResult(null);
    try { setResult(await importAccounts(file)); }
    catch (e) { setError(e.response?.data?.detail || e.message || "Import failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="aw-import-root">
      <style>{IMPORT_CSS}</style>

      {/* Header */}
      <div className="aw-import-header">
        <div className="aw-import-icon-wrap">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round">
            <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/>
          </svg>
        </div>
        <div>
          <h2 className="aw-import-title">Import Accounts</h2>
          <p className="aw-import-sub">Upload an Excel file to add property accounts to Agent Watch</p>
        </div>
      </div>

      {/* Supported columns info */}
      <div className="aw-import-info">
        <div className="aw-import-info-label">Supported columns</div>
        <div className="aw-import-chips">
          {["Parcel Acct Num", "Assessor(s)", "Tax Year", "Company Name", "Site Address1",
            "Account Handler", "Prop Type", "MV", "Top Level Company Name"].map(c => (
            <span key={c} className="aw-import-chip">{c}</span>
          ))}
        </div>
      </div>

      {/* Dropzone */}
      <div className={`aw-import-drop ${dragOver ? "aw-import-drop-over" : ""} ${file ? "aw-import-drop-has" : ""}`}
        onClick={() => inputRef.current?.click()}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) setFile(f); }}
      >
        <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={e => setFile(e.target.files[0])} />

        {file ? (
          <div className="aw-import-file">
            <div className="aw-import-file-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.5"><path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
            </div>
            <div>
              <div className="aw-import-file-name">{file.name}</div>
              <div className="aw-import-file-size">{(file.size / 1024).toFixed(0)} KB</div>
            </div>
            <button className="aw-import-file-clear" onClick={e => { e.stopPropagation(); setFile(null); setResult(null); }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        ) : (
          <>
            <div className="aw-import-drop-icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round">
                <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/>
              </svg>
            </div>
            <div className="aw-import-drop-text">Drop your Excel file here</div>
            <div className="aw-import-drop-hint">or click to browse &middot; .xlsx, .xls, .csv</div>
          </>
        )}
      </div>

      {/* Actions */}
      <div className="aw-import-actions">
        <button className="aw-btn-primary" onClick={handleUpload} disabled={!file || loading}>
          {loading ? (
            <><span className="aw-import-spinner" /> Importing...</>
          ) : "Import Accounts"}
        </button>
        <button className="aw-btn" onClick={() => onDone?.()}>Cancel</button>
      </div>

      {/* Error */}
      {error && <div className="aw-import-error">{error}</div>}

      {/* Results */}
      {result && (
        <div className="aw-import-results">
          <div className="aw-import-results-grid">
            <ResultCard n={result.imported} label="Imported" color="#22c55e" icon="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            <ResultCard n={result.skipped} label="Skipped" color="#f59e0b" icon="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            <ResultCard n={result.errors?.length || 0} label="Errors" color="#ef4444" icon="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </div>

          {result.errors?.length > 0 && (
            <div className="aw-import-err-list">
              {result.errors.slice(0, 5).map((e, i) => <div key={i} className="aw-import-err-item">{e}</div>)}
            </div>
          )}

          <button className="aw-btn-primary" style={{ marginTop: 16, width: "100%" }} onClick={() => onDone?.()}>
            Go to Dashboard
          </button>
        </div>
      )}
    </div>
  );
}

function ResultCard({ n, label, color, icon }) {
  return (
    <div className="aw-import-rcard" style={{ "--rc": color }}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d={icon}/></svg>
      <div className="aw-import-rcard-n" style={{ color }}>{n}</div>
      <div className="aw-import-rcard-l">{label}</div>
    </div>
  );
}

const IMPORT_CSS = `
.aw-import-root{max-width:560px;margin:0 auto;animation:aw-in .4s var(--ease) both;}

.aw-import-header{display:flex;align-items:center;gap:14px;margin-bottom:24px;}
.aw-import-icon-wrap{
  width:48px;height:48px;border-radius:10px;flex-shrink:0;
  display:flex;align-items:center;justify-content:center;
  background:var(--accent-g);border:1px solid rgba(139,35,50,.2);
}
.aw-import-title{font-size:20px;font-weight:800;margin:0;line-height:1.2;}
.aw-import-sub{font-size:13px;color:var(--dim);margin:3px 0 0;}

.aw-import-info{
  padding:12px 14px;border-radius:8px;margin-bottom:18px;
  background:var(--surface);border:1px solid var(--border-l);
}
.aw-import-info-label{font-size:10.5px;font-weight:700;color:var(--dim);text-transform:uppercase;letter-spacing:.5px;margin-bottom:8px;}
.aw-import-chips{display:flex;flex-wrap:wrap;gap:4px;}
.aw-import-chip{
  padding:3px 8px;border-radius:4px;font-size:10.5px;font-weight:600;
  background:rgba(107,101,119,.08);color:var(--muted);border:1px solid var(--border-l);
}

/* Dropzone */
.aw-import-drop{
  border:2px dashed var(--border);border-radius:10px;padding:40px 24px;
  text-align:center;cursor:pointer;background:var(--surface);
  transition:all .25s var(--ease);position:relative;overflow:hidden;
}
.aw-import-drop::before{
  content:'';position:absolute;inset:0;border-radius:8px;
  background:linear-gradient(135deg,transparent 40%,var(--accent-g) 100%);
  opacity:0;transition:opacity .3s var(--ease);pointer-events:none;
}
.aw-import-drop:hover{border-color:var(--muted);}
.aw-import-drop:hover::before{opacity:1;}
.aw-import-drop-over{border-color:var(--accent) !important;background:var(--accent-g) !important;}
.aw-import-drop-over::before{opacity:1 !important;}
.aw-import-drop-has{border-style:solid;border-color:var(--accent);padding:16px 20px;}
.aw-import-drop-icon{color:var(--dim);margin-bottom:12px;transition:color .2s var(--ease);}
.aw-import-drop:hover .aw-import-drop-icon{color:var(--muted);}
.aw-import-drop-over .aw-import-drop-icon{color:var(--accent) !important;}
.aw-import-drop-text{font-size:14px;font-weight:600;color:var(--text);margin-bottom:4px;}
.aw-import-drop-hint{font-size:12px;color:var(--dim);}

/* File selected */
.aw-import-file{display:flex;align-items:center;gap:12px;text-align:left;position:relative;z-index:1;}
.aw-import-file-icon{
  width:40px;height:40px;border-radius:8px;flex-shrink:0;
  display:flex;align-items:center;justify-content:center;
  background:var(--accent-g);border:1px solid rgba(139,35,50,.18);
}
.aw-import-file-name{font-size:13px;font-weight:700;color:var(--accent);}
.aw-import-file-size{font-size:11.5px;color:var(--dim);}
.aw-import-file-clear{
  margin-left:auto;background:none;border:none;color:var(--dim);cursor:pointer;
  padding:4px;border-radius:4px;transition:all .15s var(--ease);
}
.aw-import-file-clear:hover{color:var(--text);background:rgba(240,234,237,.04);}

.aw-import-actions{display:flex;gap:8px;margin-top:18px;}
.aw-import-spinner{
  display:inline-block;width:13px;height:13px;border:2px solid rgba(240,234,237,.3);
  border-top-color:#f0eaed;border-radius:50%;animation:aw-spin .6s linear infinite;
}

.aw-import-error{
  margin-top:12px;padding:10px 14px;border-radius:7px;
  background:rgba(232,69,58,.08);border:1px solid rgba(232,69,58,.15);
  color:#e8736a;font-size:13px;
}

/* Results */
.aw-import-results{
  margin-top:18px;padding:20px;border-radius:10px;
  background:var(--surface);border:1px solid var(--border-l);
  animation:aw-in .3s var(--ease) both;
}
.aw-import-results-grid{display:flex;gap:10px;}
.aw-import-rcard{
  flex:1;padding:14px 12px;border-radius:8px;text-align:center;
  background:rgba(20,18,24,.2);border:1px solid var(--border-l);
}
.aw-import-rcard-n{font-size:22px;font-weight:800;line-height:1;margin:6px 0 2px;}
.aw-import-rcard-l{font-size:10px;font-weight:700;color:var(--dim);text-transform:uppercase;letter-spacing:.4px;}

.aw-import-err-list{margin-top:12px;padding:8px 10px;border-radius:5px;background:rgba(232,69,58,.04);border:1px solid rgba(232,69,58,.08);}
.aw-import-err-item{font-size:11px;color:#e8736a;padding:2px 0;}
`;
